import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useParams, NavLink } from 'react-router-dom'
import { useRecoilState, useSetRecoilState } from 'recoil'
import { endOfDay } from 'date-fns'
import clsx from 'clsx'
import IconChevronDown from '@/components/icons/IconChevronDown'
import TabNavigation from '@/components/TabNavigation'
import ConnectWalletView from '@/components/ConnectWalletView'
import DatePicker from '@/components/DatePicker'
import Pagination from '@/components/Pagination'
import TableListNoDataRow from '@/components/TableListNoDataRow'
import MarketFilter from '@/components/orders/MarketFilter'
import TransactionStatusFilter from '@/components/wallet/TransactionStatusFilter'
import AssetFilter from '@/components/wallet/AssetFilter'
import AssetHistoryRow from '@/components/wallet/AssetHistoryRow'
import SettlementHistoryRow from '@/components/wallet/SettlementHistoryRow'
import FundingFeeHistoryRow from '@/components/wallet/FundingFeeHistoryRow'
import { useWalletSelector } from '@/contexts/WalletSelectorContext'
import { blockExplorerUrlState } from '@/recoil/Network'
import { popupNotificationListState } from '@/recoil/Notification'
import { tokenListState } from '@/recoil/Wallet'
import fetcher from '@/api/fetcher'
import {
  DEFAULT_STATUS_FILTER,
  MAX_HISTORY_ROW,
  AUTO_RETRY_TIME,
  HISTORY_TYPE_LIST,
  HISTORY_TYPE_MAP,
  ASSET_HISTORY_TYPE_LIST,
  SETTLEMENT_HISTORY_TYPE_LIST,
  FUNDING_FEE_HISTORY_TYPE_LIST,
  TRANSACTION_STATUS_FILTER_LIST,
  HISTORY_DATA_COLUMN_MAP
} from '@/constants/walletHistory'

import './WalletHistory.css'

export default function WalletHistory() {
  const navigate = useNavigate()
  const { type } = useParams()
  const { accountId } = useWalletSelector()

  const [blockExplorerUrl] = useRecoilState(blockExplorerUrlState)
  const [tokenList] = useRecoilState(tokenListState)
  const setPopupNotification = useSetRecoilState(popupNotificationListState)
  const [isLoading, setIsLoading] = useState(false)
  const [typeFilter, setTypeFiler] = useState(
    HISTORY_TYPE_LIST.includes(type) ? type.toUpperCase() : 'DEPOSIT'
  )
  const [dateRange, setDateRange] = useState([null, null])
  const [assetFilter, setAssetFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState(DEFAULT_STATUS_FILTER)
  const [marketFilter, setMarketFilter] = useState('')
  const [page, setPage] = useState(0)
  const [totalPage, setTotalPage] = useState(0)
  const [historyList, setHistoryList] = useState([])

  const retryTimestampRef = useRef(null)

  const isAssetHistory = ASSET_HISTORY_TYPE_LIST.includes(typeFilter)
  const isSettlementHistory = SETTLEMENT_HISTORY_TYPE_LIST.includes(typeFilter)
  const isFundingFeeHistory = FUNDING_FEE_HISTORY_TYPE_LIST.includes(typeFilter)

  const getHistory = useCallback(
    params => {
      let historyFetcherKey = null
      if (isAssetHistory) {
        historyFetcherKey = 'getAssetHistory'
      } else if (isSettlementHistory) {
        historyFetcherKey = 'getSettlementHistory'
      } else if (isFundingFeeHistory) {
        historyFetcherKey = 'getrFundingFeeHistory'
      }
      return historyFetcherKey
        ? fetcher[historyFetcherKey](params).then(({ meta, rows }) => {
            setTotalPage(Math.ceil(meta.total / MAX_HISTORY_ROW))
            setHistoryList(rows)
          })
        : Promise.resolve()
    },
    [isAssetHistory, isSettlementHistory, isFundingFeeHistory]
  )

  const setFilter = (key, value) => {
    setPage(0)
    if (key === 'date') {
      return setDateRange(value)
    }
    if (key === 'asset') {
      return setAssetFilter(value)
    }
    if (key === 'status') {
      return setStatusFilter(value)
    }
    if (key === 'market') {
      return setMarketFilter(value)
    }
  }

  const resetFiler = () => {
    setDateRange([null, null])
    setAssetFilter('')
    setStatusFilter(DEFAULT_STATUS_FILTER)
    setMarketFilter('')
    setPage(0)
  }

  useEffect(() => {
    const paramsType = type.toUpperCase()
    if (paramsType === typeFilter) {
      return
    }

    if (!HISTORY_TYPE_LIST.includes(type)) {
      return navigate('/wallet/history/deposit')
    }
    setTypeFiler(paramsType)
    setTotalPage(0)
    setHistoryList([])
    resetFiler()
  }, [navigate, type, typeFilter])

  useEffect(() => {
    if (!accountId) {
      return
    }
    let autoRetryTimer
    const [startDate, endDate] = dateRange
    const params = {
      page: page + 1,
      size: MAX_HISTORY_ROW
    }

    if (startDate) {
      params.start_t = startDate.getTime()
    }
    if (endDate) {
      params.end_t = endOfDay(endDate).getTime()
    }

    if (isAssetHistory) {
      params.side = typeFilter
      if (assetFilter !== '') {
        params.token = assetFilter
      }
      if (statusFilter !== DEFAULT_STATUS_FILTER) {
        params.status = statusFilter.toUpperCase()
      }
    }

    if (isFundingFeeHistory && marketFilter !== '') {
      params.symbol = marketFilter
    }

    setIsLoading(true)
    getHistory(params)
      .catch(e => {
        if (e.response?.data?.code === -1003) {
          const ts = retryTimestampRef.current || Date.now() + AUTO_RETRY_TIME
          retryTimestampRef.current = ts

          autoRetryTimer = setTimeout(() => {
            getHistory(params)
            retryTimestampRef.current = null
          }, ts - Date.now())
        }
        const errorMessage = e.response?.data?.message
        errorMessage &&
          setPopupNotification({
            notification: {
              key: Date.now(),
              status: 'error',
              message: errorMessage
            }
          })
      })
      .finally(() => setIsLoading(false))

    return () => clearTimeout(autoRetryTimer)
  }, [
    accountId,
    isAssetHistory,
    isFundingFeeHistory,
    typeFilter,
    assetFilter,
    statusFilter,
    marketFilter,
    dateRange,
    page,
    getHistory,
    setPopupNotification
  ])

  const HistoryTypeTabList = HISTORY_TYPE_LIST.map(key => (
    <NavLink
      key={key}
      className="wallet-history-type-tab"
      to={`/wallet/history/${key}`}
    >
      {HISTORY_TYPE_MAP[key] || key}
    </NavLink>
  ))

  const HistoryHeaderList = HISTORY_DATA_COLUMN_MAP[typeFilter]?.map(
    ({ text, align }) => (
      <th key={text} className={`table-list-cell align-${align}`}>
        {text}
      </th>
    )
  )

  const HistoryList = historyList.map((history, index) => {
    if (isAssetHistory) {
      return (
        <AssetHistoryRow
          key={history.uuid}
          accountId={accountId}
          blockExplorerUrl={blockExplorerUrl}
          history={history}
        />
      )
    }

    if (isSettlementHistory) {
      return <SettlementHistoryRow key={history.id} history={history} />
    }

    if (isFundingFeeHistory) {
      return (
        <FundingFeeHistoryRow
          key={`${history.updated_time}${index}`}
          history={history}
        />
      )
    }

    return null
  })

  const historyListNotEmpty = historyList.length > 0
  const hasFilter =
    !dateRange.every(v => v === null) ||
    assetFilter !== '' ||
    statusFilter !== DEFAULT_STATUS_FILTER ||
    marketFilter !== ''

  return (
    <div className="wallet-history-container">
      <TabNavigation />
      {accountId ? (
        <>
          <div className="back-to-wallet-bar">
            <div
              className="back-to-wallet-link"
              onClick={() => navigate('/wallet')}
            >
              <IconChevronDown className="icon-back" />
              <span className="back-to-wallet-text">
                Back to Wallet Overview
              </span>
            </div>
          </div>
          <div
            className={clsx('card wallet-history', {
              loading: isLoading
            })}
          >
            <div className="wallet-history-title">Wallet History</div>
            <div className="wallet-history-type-container">
              {HistoryTypeTabList}
            </div>
            <div className="wallet-history-filter-container">
              <div className="wallet-history-filter">
                <DatePicker
                  dateRange={dateRange}
                  setDateRange={date => setFilter('date', date)}
                />
                {isAssetHistory && (
                  <>
                    <TransactionStatusFilter
                      value={statusFilter}
                      optionList={TRANSACTION_STATUS_FILTER_LIST}
                      onUpdate={status => setFilter('status', status)}
                    />
                    <AssetFilter
                      value={assetFilter}
                      optionList={tokenList}
                      onUpdate={asset => setFilter('asset', asset)}
                    />
                  </>
                )}
                {isFundingFeeHistory && (
                  <MarketFilter
                    value={marketFilter}
                    onUpdate={symbol => setFilter('market', symbol)}
                  />
                )}
              </div>
              {hasFilter && (
                <div className="reset-filter-button-text" onClick={resetFiler}>
                  Clear
                </div>
              )}
            </div>
            <table className="table-list">
              <thead>
                <tr className="table-list-header noselect">
                  {HistoryHeaderList}
                </tr>
              </thead>
              <tbody>
                {historyListNotEmpty ? HistoryList : <TableListNoDataRow />}
              </tbody>
            </table>
            {totalPage > 0 && (
              <Pagination
                page={page}
                pageCount={totalPage}
                onPageChange={setPage}
              />
            )}
          </div>
        </>
      ) : (
        <div className="card noselect">
          <ConnectWalletView />
        </div>
      )}
    </div>
  )
}
